<script>
  /** @type {{ padding?: number, slot?: import('svelte').Snippet<[{ position: number, x: number, y: number, inside: boolean, center: boolean }]> }} */
  const { padding = 0.1, slot } = $props();

  /** @type {HTMLDivElement} */
  let container;

  /** @type {boolean} */
  let isVertical = $state(false);

  /** @type {{ width: number, height: number }} */
  let containerRect = $state({ width: 0, height: 0 });

  $effect(() => {
    if (!container) return;
    const r = container.getBoundingClientRect();
    isVertical = r.height > r.width;
    containerRect = { width: r.width, height: r.height };

    const ro = new ResizeObserver(() => {
      const r = container.getBoundingClientRect();
      isVertical = r.height > r.width;
      containerRect = { width: r.width, height: r.height };
    });
    ro.observe(container);
    return () => ro.disconnect();
  });

  /**
   * Stadium-shaped felt edge, computed by arc-length parameterization.
   *
   * The felt is `<rect x="114" y="114" width="772" height="372" rx="186">`.
   * With rx = height/2, left/right sides are semicircles centered at (300,300) and (700,300).
   * Straights connect arc endpoints: bottom (300,486)→(700,486), top (700,114)→(300,114).
   * Straight length = 400 (distance between semicircle centers).
   *
   * When offsetting inward/outward: semicircle centers stay FIXED, only radius changes.
   * Straight length is unchanged. Only the y-coordinates of horizontal straights shift.
   */

  /**
   * Parameterize a stadium by normalized arc-length t ∈ [0, 1).
   *
   * For axis='x' with clockwise=true (default):
   *   Starts at bottom-center, goes CLOCKWISE (LEFT along bottom → up left side →
   *   RIGHT across top → down right side → back to bottom-center).
   *
   * For axis='x' with clockwise=false:
   *   Starts at bottom-center, goes COUNTERCLOCKWISE (RIGHT along bottom → up right side →
   *   LEFT across top → down left side → back to bottom-center).
   */
  function stadiumPoint(t, straightLen, r, cx, cy, axis, clockwise = true) {
    t = ((t % 1) + 1) % 1;
    const arcLen = Math.PI * r;
    const perimeter = 2 * straightLen + 2 * arcLen;
    const half = straightLen / 2;

    if (axis === "x") {
      if (clockwise) {
        // Clockwise from bottom-center: go LEFT along bottom first
        let d = (t * perimeter + half) % perimeter;

        // 1. Bottom straight (right → left)
        if (d < straightLen) {
          return { x: cx + half - d, y: cy + r };
        }
        d -= straightLen;

        // 2. Left semicircle (bottom → top through left)
        if (d < arcLen) {
          const a = (d / arcLen) * Math.PI;
          return { x: cx - half - r * Math.sin(a), y: cy + r * Math.cos(a) };
        }
        d -= arcLen;

        // 3. Top straight (left → right)
        if (d < straightLen) {
          return { x: cx - half + d, y: cy - r };
        }
        d -= straightLen;

        // 4. Right semicircle (top → bottom through right)
        const a = (d / arcLen) * Math.PI;
        return { x: cx + half + r * Math.sin(a), y: cy - r * Math.cos(a) };
      } else {
        // Counterclockwise from bottom-center: go RIGHT along bottom first (original)
        let d = (t * perimeter + half) % perimeter;

        // 1. Bottom straight (left → right)
        if (d < straightLen) {
          return { x: cx - half + d, y: cy + r };
        }
        d -= straightLen;

        // 2. Right semicircle (bottom → top through right)
        if (d < arcLen) {
          const a = (d / arcLen) * Math.PI;
          return { x: cx + half + r * Math.sin(a), y: cy + r * Math.cos(a) };
        }
        d -= arcLen;

        // 3. Top straight (right → left)
        if (d < straightLen) {
          return { x: cx + half - d, y: cy - r };
        }
        d -= straightLen;

        // 4. Left semicircle (top → bottom through left)
        const a = (d / arcLen) * Math.PI;
        return { x: cx - half - r * Math.sin(a), y: cy - r * Math.cos(a) };
      }
    } else {
      // axis === 'y' – kept for backward compatibility
      let d = (t * perimeter + half) % perimeter;

      if (d < straightLen) {
        return { x: cx - r, y: cy + half - d };
      }
      d -= straightLen;

      if (d < arcLen) {
        const a = (d / arcLen) * Math.PI;
        return { x: cx - r * Math.cos(a), y: cy - half - r * Math.sin(a) };
      }
      d -= arcLen;

      if (d < straightLen) {
        return { x: cx + r, y: cy - half + d };
      }
      d -= straightLen;

      const a = (d / arcLen) * Math.PI;
      return { x: cx + r * Math.cos(a), y: cy + half + r * Math.sin(a) };
    }
  }

  /** Compute N evenly-spaced slot positions (% of container) */
  function computeSlots(
    straightLen,
    r,
    cx,
    cy,
    axis,
    count,
    cw,
    ch,
    clockwise = true,
  ) {
    return Array.from({ length: count }, (_, i) => {
      const pt = stadiumPoint(
        i / count,
        straightLen,
        r,
        cx,
        cy,
        axis,
        clockwise,
      );
      return { x: (pt.x / cw) * 100, y: (pt.y / ch) * 100 };
    });
  }

  /**
   * Compute slot positions for the vertical (portrait) mode.
   *
   * The vertical SVG draws the table with matrix(0 1 1 0 0 0) which swaps x and y.
   * So we compute positions using the horizontal geometry (which stays consistent
   * with the actual table SVG elements) and then swap coordinates.
   *
   * To put position 0 at the visual bottom-center of the vertical table:
   *   The bottom-center of the vertical viewBox corresponds to the rightmost point
   *   of the horizontal geometry (since the matrix transform rotates 90° clockwise).
   *   The rightmost point is at t = 0.25 in the CCW parameterization.
   *   So we shift t by 0.25, compute with the CCW version (since the
   *   x↔y swap reverses orientation, keeping the overall clockwise direction),
   *   then swap (x,y) → (y,x) to match the SVG transform.
   */
  function computeVerticalSlots(straightLen, r, cx, cy, count, cw, ch) {
    return Array.from({ length: count }, (_, i) => {
      // Shift by 0.25: horizontal rightmost point → vertical bottom-center
      const t = (i / count + 0.25) % 1;
      // Use CCW (clockwise=false) because the coordinate swap reverses orientation
      const pt = stadiumPoint(t, straightLen, r, cx, cy, "x", false);
      // Swap coordinates to match the SVG matrix(0 1 1 0 0 0) transform
      // pt.y is in [0, 600] → maps to vertical container width (cw=600)
      // pt.x is in [0, 1000] → maps to vertical container height (ch=1000)
      return { x: (pt.y / cw) * 100, y: (pt.x / ch) * 100 };
    });
  }

  /**
   * Compute the actual rendered SVG viewBox content area within the container,
   * as percentages of the container. This accounts for:
   * 1. The SVG element being smaller than the container (due to padding)
   * 2. The viewBox being letterboxed within the SVG element
   *    (preserveAspectRatio="xMidYMid meet")
   */
  function computeSvgContentRect(cw, ch, vbw, vbh, pct) {
    if (cw === 0 || ch === 0) return null;
    const svgW = cw * pct;
    const svgH = ch * pct;
    const scale = Math.min(svgW / vbw, svgH / vbh);
    const contentW = vbw * scale;
    const contentH = vbh * scale;
    const svgLeft = (cw - svgW) / 2;
    const svgTop = (ch - svgH) / 2;
    const cx = svgLeft + (svgW - contentW) / 2;
    const cy = svgTop + (svgH - contentH) / 2;
    return {
      left: (cx / cw) * 100,
      top: (cy / ch) * 100,
      width: (contentW / cw) * 100,
      height: (contentH / ch) * 100,
    };
  }

  // ——— Table geometry (shared by both modes) ———
  // The SVG always draws the same horizontal table elements.
  // In vertical mode the <g> is transformed with matrix(0 1 1 0 0 0).
  // straightLen=400 (between semicircle centers), r=186, center=(500,300)
  // inner: r=156, outer: r=246 (straightLen unchanged)
  const H_CX = 500,
    H_CY = 300,
    H_SL = 400,
    H_R = 186;

  /** @type {{ x: number, y: number }[]} */
  const slots = $derived(
    isVertical
      ? computeVerticalSlots(H_SL, H_R + 60, H_CX, H_CY, 9, 600, 1000)
      : computeSlots(H_SL, H_R + 60, H_CX, H_CY, "x", 9, 1000, 600, true),
  );

  /** @type {{ x: number, y: number }[]} */
  const innerSlots = $derived(
    isVertical
      ? computeVerticalSlots(H_SL, H_R - 60, H_CX, H_CY, 9, 600, 1000)
      : computeSlots(H_SL, H_R - 60, H_CX, H_CY, "x", 9, 1000, 600, true),
  );

  const svgPct = 1 - padding;
  const vbw = $derived(isVertical ? 600 : 1000);
  const vbh = $derived(isVertical ? 1000 : 600);

  /**
   * The SVG viewBox content's rendered rect within the container,
   * expressed as percentages of the container.
   * Falls back to a padding-only estimate when container isn't measured yet.
   */
  const contentRect = $derived.by(() => {
    const { width: cw, height: ch } = containerRect;
    if (cw > 0 && ch > 0) {
      const r = computeSvgContentRect(cw, ch, vbw, vbh, svgPct);
      if (r) return r;
    }
    // Fallback: just account for padding (ignores letterboxing)
    const inset = (1 - svgPct) / 2 * 100;
    return { left: inset, top: inset, width: svgPct * 100, height: svgPct * 100 };
  });

  /** Transform viewBox-relative percentages to container-relative percentages */
  function toContainerPos(slot) {
    return {
      x: contentRect.left + slot.x * contentRect.width / 100,
      y: contentRect.top + slot.y * contentRect.height / 100,
    };
  }

  const positionedSlots = $derived(slots.map(toContainerPos));
  const positionedInnerSlots = $derived(innerSlots.map(toContainerPos));
</script>

<div
  bind:this={container}
  class="relative w-full h-full text-[var(--poker-text)] bg-[var(--poker-bg)]"
>
  <svg
    viewBox={isVertical ? "0 0 600 1000" : "0 0 1000 600"}
    xmlns="http://www.w3.org/2000/svg"
    class="absolute inset-0 m-auto"
    style="width: {(1 - padding) * 100}%; height: {(1 - padding) * 100}%;"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <!-- Heavy Ambient Glow Filter (for deep background neon) -->
      <filter id="glow-heavy" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="25" result="blur" />
      </filter>

      <!-- Medium Intense Glow Filter -->
      <filter id="glow-medium" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="10" result="blur" />
      </filter>

      <!-- Sharp/Soft Core Glow Filter -->
      <filter id="glow-sharp" x="-10%" y="-10%" width="120%" height="120%">
        <feGaussianBlur stdDeviation="3" result="blur" />
      </filter>

      <!-- Felt Radial Gradient -->
      <radialGradient id="felt-grad" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="var(--poker-felt-center)" />
        <stop offset="75%" stop-color="var(--poker-felt-mid)" />
        <stop offset="100%" stop-color="var(--poker-felt-edge)" />
      </radialGradient>
    </defs>

    <g transform={isVertical ? "matrix(0 1 1 0 0 0)" : null}>
      <g id="poker-table-only">
        <!-- 1. AMBIENT NEON SHADOW -->
        <rect
          x="95"
          y="95"
          width="810"
          height="410"
          rx="205"
          fill="none"
          stroke="var(--poker-neon-glow)"
          stroke-width="40"
          opacity="0.2"
          filter="url(#glow-heavy)"
        />

        <!-- 2. MID-INTENSITY NEON GLOW RING -->
        <rect
          x="95"
          y="95"
          width="810"
          height="410"
          rx="205"
          fill="none"
          stroke="var(--poker-neon-core)"
          stroke-width="12"
          opacity="0.6"
          filter="url(#glow-medium)"
        />

        <!-- 3. CRISP NEON CORE -->
        <rect
          x="95"
          y="95"
          width="810"
          height="410"
          rx="205"
          fill="none"
          stroke="var(--poker-light-stripe)"
          stroke-width="2.5"
          filter="url(#glow-sharp)"
        />

        <!-- 4. OUTER METALLIC FRAME/BEZEL -->
        <rect
          x="105"
          y="105"
          width="790"
          height="390"
          rx="195"
          fill="none"
          stroke="var(--poker-frame-outer)"
          stroke-width="12"
        />
        <rect
          x="105"
          y="105"
          width="790"
          height="390"
          rx="195"
          fill="none"
          stroke="var(--poker-frame-inner)"
          stroke-width="2"
          opacity="0.5"
        />

        <!-- 5. PERIODIC BRIGHT & SOFT GLOWING LIGHT STRIPS (Volumetric Stack) -->
        <!-- Outer Soft Halo -->
        <rect
          x="105"
          y="105"
          width="790"
          height="390"
          rx="195"
          fill="none"
          stroke="var(--poker-light-stripe)"
          stroke-width="14"
          stroke-dasharray="50 150"
          stroke-dashoffset="25"
          stroke-linecap="round"
          opacity="0.35"
          filter="url(#glow-heavy)"
        />

        <!-- Mid-level Glow Diffusion -->
        <rect
          x="105"
          y="105"
          width="790"
          height="390"
          rx="195"
          fill="none"
          stroke="var(--poker-light-stripe)"
          stroke-width="8"
          stroke-dasharray="50 150"
          stroke-dashoffset="25"
          stroke-linecap="round"
          opacity="0.6"
          filter="url(#glow-medium)"
        />

        <!-- Bright Soft Core -->
        <rect
          x="105"
          y="105"
          width="790"
          height="390"
          rx="195"
          fill="none"
          stroke="var(--poker-light-stripe)"
          stroke-width="3"
          stroke-dasharray="50 150"
          stroke-dashoffset="25"
          stroke-linecap="round"
          opacity="0.95"
          filter="url(#glow-sharp)"
        />

        <!-- 6. TABLE FELT -->
        <rect
          x="114"
          y="114"
          width="772"
          height="372"
          rx="186"
          fill="url(#felt-grad)"
        />

        <!-- 7. INNER FELT BORDER & GLOW TRACING -->
        <!-- Heavy Inner Glow -->
        <rect
          x="114"
          y="114"
          width="772"
          height="372"
          rx="186"
          fill="none"
          stroke="var(--poker-inner-border-glow)"
          stroke-width="8"
          opacity="0.3"
          filter="url(#glow-medium)"
        />
        <!-- Bright Inner Pinstripe Core -->
        <rect
          x="114"
          y="114"
          width="772"
          height="372"
          rx="186"
          fill="none"
          stroke="var(--poker-inner-border-core)"
          stroke-width="1.5"
          filter="url(#glow-sharp)"
        />
      </g>
    </g>
  </svg>

  {#if slot}
    <!-- Center slot -->
    <div
      class="absolute transition-all duration-300"
      style="left: {contentRect.left + 50 * contentRect.width / 100}%; top: {contentRect.top + 50 * contentRect.height / 100}%; transform: translate(-50%, -50%); z-index: 1;"
    >
      {@render slot({ position: -1, x: 50, y: 50, inside: true, center: true })}
    </div>

    {#each slots as s, i}
      <!-- Outside the table (60px outward from felt edge) -->
      <div
        class="absolute transition-all duration-300"
        style="left: {positionedSlots[i].x}%; top: {positionedSlots[i].y}%; transform: translate(-50%, -50%); z-index: 1;"
      >
        {@render slot({
          position: i,
          x: s.x,
          y: s.y,
          inside: false,
          center: false,
        })}
      </div>
      <!-- Inside the table (60px inward from felt edge, on the felt) -->
      <div
        class="absolute transition-all duration-300"
        style="left: {positionedInnerSlots[i].x}%; top: {positionedInnerSlots[i]
          .y}%; transform: translate(-50%, -50%);"
      >
        {@render slot({
          position: i,
          x: innerSlots[i].x,
          y: innerSlots[i].y,
          inside: true,
          center: false,
        })}
      </div>
    {/each}
  {/if}
</div>

<style>
  /* Ensure the SVG glow filters render with enough space */
  svg {
    overflow: visible;
  }
</style>
